import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const GOLD = [212, 175, 55];
const DARK = [16, 20, 34];
const GREY = [130, 130, 130];

export const CONDITION_LABEL = {
  good: "Good", minor_damage: "Minor Damage", damaged: "Damaged",
  wet: "Wet", crushed: "Crushed", opened_tampered: "Opened / Tampered", other: "Other",
};

export const ISSUE_LABEL = {
  damaged_pallet: "Damaged Pallet", damaged_boxes: "Damaged Boxes",
  wet_boxes: "Wet Boxes", crushed_boxes: "Crushed Boxes",
  opened_tampered_boxes: "Opened / Tampered Boxes", missing_boxes: "Missing Boxes",
  extra_boxes: "Extra Boxes", pallet_count_mismatch: "Pallet Count Mismatch",
  incorrect_shipment: "Incorrect Shipment", delivery_note_mismatch: "Delivery Note Mismatch",
  other: "Other",
};

export const STATUS_LABEL = {
  draft: "Draft", counting: "Counting", review: "Review",
  accepted: "Accepted", accepted_with_issues: "Accepted With Issues", cancelled: "Cancelled",
};

export const SHIPMENT_TYPE_LABEL = {
  pallets: "Pallets", boxes: "Boxes", pallets_and_boxes: "Pallets + Boxes",
};

async function loadLogo() {
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = "/hos-logo.webp";
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    canvas.getContext("2d").drawImage(img, 0, 0);
    return canvas.toDataURL("image/png");
  } catch { return null; }
}

async function photoToDataUrl(p) {
  if (!p) return null;
  if (p.data_url) return p.data_url;
  if (!p.url) return null;
  try {
    const token = window.localStorage.getItem("hos_token");
    const backend = window.location.origin;
    const abs = p.url.startsWith("http") ? p.url : `${backend}${p.url}`;
    const resp = await fetch(abs, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function drawHeader(doc, r, logo) {
  const w = doc.internal.pageSize.getWidth();
  doc.setFillColor(...DARK); doc.rect(0, 0, w, 26, "F");
  if (logo) doc.addImage(logo, "PNG", 12, 5, 16, 16);
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text("HOUSE OF SPELLS — INVENTORY RECEIVING REPORT", 32, 12);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(`${r.reference_number} · ${r.store_name} · ${fmt(r.arrival_at)}`, 32, 19);
}

function drawFooter(doc, page, total) {
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  doc.setTextColor(...GREY); doc.setFontSize(8);
  doc.text(`Page ${page} of ${total}`, w - 25, h - 8);
  doc.text(`Generated ${new Date().toLocaleString()}`, 12, h - 8);
}

export default async function generateReceivingPdf(r) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const logo = await loadLogo();
  drawHeader(doc, r, logo);

  let y = 34;
  doc.setTextColor(...DARK);
  doc.setFont("helvetica", "bold"); doc.setFontSize(16);
  doc.text(`Receiving ${r.reference_number}`, 12, y);
  y += 6;

  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text(STATUS_LABEL[r.status] || r.status, 12, y);
  y += 6;

  // Shipment details
  const st = r.shipment_type || "pallets";
  const details = [
    ["Store", r.store_name || "—"],
    ["Supplier", r.supplier_name || "—"],
    ["Shipment Type", SHIPMENT_TYPE_LABEL[st] || st],
    ["Purchase Order", r.purchase_order_number || "—"],
    ["Invoice", r.invoice_number || "—"],
    ["Arrival", fmt(r.arrival_at)],
    ["Carrier", r.carrier_name || "—"],
    ["Driver", r.driver_name || "—"],
    ["Driver Phone", r.driver_phone || "—"],
    ["Truck / Vehicle", r.truck_number || "—"],
    ["Trailer", r.trailer_number || "—"],
    ["Delivery Ref", r.delivery_reference || "—"],
    ["Received By", r.received_by_name || "—"],
  ];
  autoTable(doc, {
    startY: y,
    head: [["Field", "Value"]],
    body: details,
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: DARK, textColor: GOLD },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 40 } },
    margin: { left: 12, right: 12 },
  });
  y = doc.lastAutoTable.finalY + 6;

  const showPallets = st === "pallets" || st === "pallets_and_boxes";
  const showLoose = st === "boxes" || st === "pallets_and_boxes";

  // Pallet table (only if pallets shipment)
  if (showPallets) {
  doc.setFont("helvetica", "bold"); doc.setFontSize(12);
  doc.setTextColor(...DARK);
  doc.text(`Pallets — ${r.total_pallets || 0} pallets · ${r.boxes_on_pallets || 0} boxes on pallets`, 12, y);
  y += 3;
  const palletRows = (r.pallets || []).map((p) => [
    p.barcode ? `Pallet ${p.pallet_number}\n⌗ ${p.barcode}` : `Pallet ${p.pallet_number}`,
    String(p.box_count || 0),
    CONDITION_LABEL[p.condition] || p.condition,
    (p.notes || "").slice(0, 80),
  ]);
  if (r.expected_pallets != null || r.expected_boxes != null) {
    palletRows.push([
      { content: "Expected", styles: { fontStyle: "bold" } },
      String(r.expected_boxes ?? "—"),
      { content: `Expected ${r.expected_pallets ?? "—"} pallets`, styles: { fontStyle: "italic" } },
      "",
    ]);
  }
  palletRows.push([
    { content: "TOTAL BOXES ON PALLETS", styles: { fontStyle: "bold" } },
    { content: String(r.boxes_on_pallets || 0), styles: { fontStyle: "bold" } },
    "", "",
  ]);
  autoTable(doc, {
    startY: y,
    head: [["Pallet", "Boxes", "Condition", "Notes"]],
    body: palletRows,
    theme: "striped",
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: DARK, textColor: GOLD },
    margin: { left: 12, right: 12 },
  });
  y = doc.lastAutoTable.finalY + 6;
  }

  // Loose boxes / grand totals
  const totalsRows = [];
  if (showPallets) totalsRows.push(["Pallets", String(r.total_pallets || 0)]);
  if (showPallets) totalsRows.push(["Boxes on Pallets", String(r.boxes_on_pallets || 0)]);
  if (showLoose) totalsRows.push(["Loose Boxes", String(r.loose_boxes || 0)]);
  totalsRows.push([
    { content: "TOTAL BOXES", styles: { fontStyle: "bold", textColor: GOLD } },
    { content: String(r.total_boxes || 0), styles: { fontStyle: "bold", textColor: GOLD } },
  ]);
  autoTable(doc, {
    startY: y,
    head: [["Received Inventory", "Count"]],
    body: totalsRows,
    theme: "grid",
    styles: { fontSize: 10, cellPadding: 2 },
    headStyles: { fillColor: DARK, textColor: GOLD },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 60 } },
    margin: { left: 12, right: 12 },
  });
  y = doc.lastAutoTable.finalY + 6;

  // Issues
  const issues = r.issues || [];
  if (issues.length) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    doc.text(`Delivery Issues (${issues.length})`, 12, y);
    y += 3;
    autoTable(doc, {
      startY: y,
      head: [["Type", "Pallet", "Qty", "Description"]],
      body: issues.map((it) => {
        const palletLbl = it.pallet_id ? (r.pallets || []).find((p) => p.id === it.pallet_id) : null;
        return [
          ISSUE_LABEL[it.issue_type] || it.issue_type,
          palletLbl ? `Pallet ${palletLbl.pallet_number}` : "—",
          String(it.quantity_affected || 0),
          (it.description || "").slice(0, 100),
        ];
      }),
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [180, 90, 40], textColor: [255, 255, 255] },
      margin: { left: 12, right: 12 },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // Signature block
  if (y > 240) { doc.addPage(); y = 20; }
  doc.setFont("helvetica", "bold"); doc.setFontSize(12);
  doc.setTextColor(...DARK);
  doc.text("Acceptance", 12, y);
  y += 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  const wrapped = doc.splitTextToSize(
    "I confirm that I have physically checked this delivery, counted the pallets and boxes recorded above, documented any visible issues, and accept the shipment based on the information entered.",
    doc.internal.pageSize.getWidth() - 24,
  );
  doc.text(wrapped, 12, y);
  y += wrapped.length * 4 + 4;
  autoTable(doc, {
    startY: y,
    head: [["Received & Accepted By", "Date", "Time", "Electronic Signature"]],
    body: [[
      r.signed_by_name || r.received_by_name || "—",
      r.signed_at ? new Date(r.signed_at).toLocaleDateString() : "—",
      r.signed_at ? new Date(r.signed_at).toLocaleTimeString() : "—",
      r.signed_by_name ? `Signed electronically by ${r.signed_by_name}` : "Not signed",
    ]],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: DARK, textColor: GOLD },
    margin: { left: 12, right: 12 },
  });
  y = doc.lastAutoTable.finalY + 4;
  doc.text(`Delivery Note Attached: ${(r.delivery_note_photos || []).length > 0 ? "Yes" : "No"}`, 12, y);
  y += 4;
  doc.text(`General Notes: ${(r.general_notes || "None").slice(0, 200)}`, 12, y);

  // Delivery note pages
  const notePhotos = r.delivery_note_photos || [];
  for (let i = 0; i < notePhotos.length; i++) {
    doc.addPage();
    doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    doc.setTextColor(...DARK);
    doc.text(`Delivery Note — Page ${i + 1}`, 12, 20);
    const url = await photoToDataUrl(notePhotos[i]);
    if (url) {
      try {
        const w = doc.internal.pageSize.getWidth() - 24;
        const h = doc.internal.pageSize.getHeight() - 40;
        doc.addImage(url, "JPEG", 12, 26, w, h, undefined, "FAST");
      } catch {}
    }
  }

  // Pallet damage photos summary
  const palletPhotos = [];
  (r.pallets || []).forEach((p) => (p.photos || []).forEach((ph) => palletPhotos.push({ ...ph, _pallet: p })));
  const issuePhotos = [];
  (r.issues || []).forEach((it) => (it.photos || []).forEach((ph) => issuePhotos.push({ ...ph, _issue: it })));
  const generalPhotos = r.general_photos || [];
  const allExtraPhotos = [
    ...palletPhotos.map((p) => ({ ...p, label: `Pallet ${p._pallet.pallet_number} · ${CONDITION_LABEL[p._pallet.condition] || p._pallet.condition}` })),
    ...issuePhotos.map((p) => ({ ...p, label: `Issue: ${ISSUE_LABEL[p._issue.issue_type] || p._issue.issue_type}` })),
    ...generalPhotos.map((p) => ({ ...p, label: "Shipment photo" })),
  ];
  if (allExtraPhotos.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    doc.setTextColor(...DARK);
    doc.text("Documentation", 12, 20);
    let cy = 28;
    const cx = 12; const size = 60;
    let col = 0;
    for (const p of allExtraPhotos) {
      const url = await photoToDataUrl(p);
      if (!url) continue;
      const x = cx + col * (size + 6);
      if (cy + size > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage(); cy = 20; col = 0;
      }
      try { doc.addImage(url, "JPEG", x, cy, size, size, undefined, "FAST"); } catch {}
      doc.setFont("helvetica", "normal"); doc.setFontSize(7);
      doc.setTextColor(60, 60, 60);
      doc.text(doc.splitTextToSize(p.label, size), x, cy + size + 3);
      col += 1;
      if (col >= 3) { col = 0; cy += size + 12; }
    }
  }

  // Footer on every page
  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    drawFooter(doc, i, total);
  }

  const filename = `House_of_Spells_Inventory_Receiving_${r.reference_number}.pdf`;
  doc.save(filename);
  return filename;
}
